import { Link } from "react-router-dom";
import { Calendar, Mic } from "lucide-react";
import { Page, Reveal, MaskedLine, Eyebrow } from "@/components/motion/Reveal";
import InquiryForm from "@/components/InquiryForm";

export default function Contact() {
  return (
    <Page data-testid="contact-page">
      {/* HERO */}
      <section data-testid="contact-hero" className="bg-ivory pt-40 pb-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Eyebrow>Contact Genesis</Eyebrow>
          <h1 className="font-serif font-medium tracking-tight text-5xl sm:text-6xl lg:text-7xl text-obsidian mt-8 leading-[1.05]">
            <MaskedLine delay={0.2}>Ready for</MaskedLine>
            <MaskedLine delay={0.35} className="italic text-gold-dark">What's Next?</MaskedLine>
          </h1>
        </div>
      </section>

      {/* FORM + OPTIONS */}
      <section data-testid="contact-main-section" className="bg-ivory pb-24 lg:pb-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-2xl sm:text-3xl text-obsidian mb-10">Send an Inquiry</h2>
              <InquiryForm testidPrefix="contact-inquiry" submitLabel="Send Inquiry" />
            </Reveal>
          </div>

          <div className="lg:col-span-5 space-y-8">
            <Reveal delay={0.15}>
              <div className="bg-obsidian text-ivory p-8 lg:p-10" data-testid="contact-consultation-card">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar size={16} className="text-gold" />
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">Book a Consultation</p>
                </div>
                <p className="font-serif text-2xl mt-4 mb-3">Let's Talk About What's Next.</p>
                <p className="text-sm font-light text-white/50 mb-8 leading-relaxed">
                  A focused conversation about where you are and where you're going.
                  Choose an available time that works for you.
                </p>
                <Link
                  to="/consultation"
                  data-testid="contact-cta-book-consultation"
                  className="inline-block bg-gold text-obsidian px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
                >
                  Book a Consultation
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="bg-navy text-ivory p-8 lg:p-10" data-testid="contact-speaking-card">
                <div className="flex items-center gap-3 mb-2">
                  <Mic size={16} className="text-gold" />
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">Planning an Event?</p>
                </div>
                <p className="font-serif text-2xl mt-4 mb-3">Book Sharon to Speak.</p>
                <p className="text-sm font-light text-white/50 mb-8 leading-relaxed">
                  Keynotes, workshops, and panels for corporate, professional,
                  university, women's leadership, and youth audiences.
                </p>
                <Link
                  to="/speaking#inquiry"
                  data-testid="contact-cta-speaking-inquiry"
                  className="inline-block border border-gold/60 text-gold px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold hover:text-obsidian transition-colors duration-300"
                >
                  Submit a Speaking Inquiry
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="border border-obsidian/10 p-8" data-testid="contact-direct-details">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-text mb-5">Direct</p>
                <a href="mailto:info@genesisconsultinghq.com" data-testid="contact-email-link" className="font-serif text-xl text-obsidian hover:text-gold-dark transition-colors">
                  info@genesisconsultinghq.com
                </a>
                <div className="border-t border-obsidian/10 mt-6 pt-6 space-y-3">
                  <p className="text-sm font-light text-stone-text">Response within 1 to 2 business days</p>
                  <p className="flex items-center gap-2 text-sm font-light text-stone-text">
                    <span className="w-2 h-2 rounded-full bg-gold inline-block" data-testid="availability-indicator" />
                    Currently accepting new clients and engagements
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </Page>
  );
}
